import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { NAV_ITEMS, isNavActive } from '../../lib/nav'

const GLYPHS = {
  profile: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  leaderboard: (
    <>
      <path d="M8 21h8M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
      <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    </>
  ),
  home: (
    <>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h5v-6h4v6h5V10" />
    </>
  ),
  library: (
    <>
      <path d="M3 5.5h7a2 2 0 0 1 2 2V20a2 2 0 0 0-2-2H3z" />
      <path d="M21 5.5h-7a2 2 0 0 0-2 2V20a2 2 0 0 1 2-2h7z" />
    </>
  ),
}

/**
 * The bottom bar: four tabs in `lib/nav.js`'s order — profile, leaderboard,
 * home, library, first item at the right in Persian, mirrored in English by
 * the document direction alone.
 *
 * V8 (design/V8-proposal.html): a capsule floating over the content, 18px
 * above the bottom edge plus the phone's home indicator, sized to its tabs
 * rather than the screen. Inactive tabs are icons only; the lit one widens
 * into the brand pill. The labels stay in the DOM as each link's accessible
 * name (`sr-only`), so screen readers and the tests read what they read
 * before. A fade from the page's ground sits behind the capsule so content
 * scrolling under it never reads as clipped; AppShell pads its main column
 * so the last card clears the capsule, and `ToastBar` sits just above it.
 *
 * Which tab is lit is `isNavActive`, not `NavLink`'s own matching (which
 * would also overwrite `aria-current`), so a lesson being practised lights
 * the library and the profile's sub-pages light the profile — hence plain
 * `Link`s.
 */
export default function BottomNav() {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20">
      <div aria-hidden="true" data-nav-scrim className="absolute inset-x-0 bottom-0 h-[150px] bg-linear-to-t from-ground from-38% to-transparent" />
      <nav
        aria-label={t('nav.ariaLabel')}
        className="capsule-float pointer-events-auto relative mx-auto mb-[calc(18px+env(safe-area-inset-bottom))] w-fit px-2"
      >
        <ul className="flex h-[66px] items-center">
          {NAV_ITEMS.map((item) => {
            const active = isNavActive(item, pathname)
            return (
              <li key={item.key}>
                <Link
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  data-nav={item.key}
                  className={`grid h-[50px] place-items-center rounded-full transition-[width,background-color,color] duration-200 ${
                    active
                      ? 'w-24 bg-brand-600 text-white shadow-[var(--shadow-brand)] dark:text-slate-900'
                      : 'w-[60px] text-ink-3 hover:text-ink-2'
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-200 ${active ? 'scale-[1.2]' : ''}`}
                  >
                    {GLYPHS[item.key]}
                  </svg>
                  <span className="sr-only">{t(`nav.${item.key}`)}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}